import { useState, useEffect, useRef } from 'react';
import { AlertCircle } from 'lucide-react';
import { useSocketContext } from '@shootsta/common-react';
import ImportFailedModal from '../components/ImportFailedModal';
import SHOOTSTA_LOGO from '../../../assets/images/shootsta-motif.svg';

type SocketStepEvent = {
  status?: 'started' | 'completed' | 'failed';
  publicProjectId?: string | null;
  error?: string | null;
  videoId?: string | null;
  setError: (error: string | null) => void;
  setLoading: (loading: boolean) => void;
  toolType?: 'BRIEF' | 'SCRIPT' | null;
  importProject: () => Promise<void>;
};

const STEPS = [
  {
    eventType: 'PUBLIC_TO_PLATFORM_PROJECT|CREATE_PROJECT',
    label: 'Creating Project'
  },
  {
    eventType: 'PUBLIC_TO_PLATFORM_PROJECT|GENERATE_BRIEF',
    label: 'Generating Brief'
  },
  {
    eventType: 'PUBLIC_TO_PLATFORM_PROJECT|GENERATE_SCRIPT',
    label: 'Generating Script'
  },
  { eventType: 'FINISHING_PROJECT_SETUP', label: 'Finishing Project Setup' }
];

export function StepLoader({
  videoId,
  error,
  setError,
  history,
  setLoading,
  importProject
}: {
  videoId?: string;
  error?: string | null;
  setError: (e: string | null) => void;
  setLoading: (loading: boolean) => void;
  history: any;
  importProject: () => Promise<void>;
}) {
  const { socket } = useSocketContext();

  const [currentLabel, setCurrentLabel] = useState('Creating Project');
  const [progress, setProgress] = useState(0);
  const [targetProgress, setTargetProgress] = useState(0);

  const hasBriefCompleted = useRef(false);
  const hasScriptCompleted = useRef(false);
  const hasCreateCompleted = useRef(false);
  const toolType = useRef<string | null>(null);
  const isProcessing = useRef(false);

  const pendingEvents = useRef<
    Array<{ eventType: string; payload: SocketStepEvent }>
  >([]);
  const params = new URLSearchParams(window.location.search);
  const expectedPublicProjectId = params.get('publicProjectId');

  const sleep = (ms: number) => new Promise<void>((res) => setTimeout(res, ms));

  const enqueueFinishingStep = (publicProjectId: string | null) => {
    pendingEvents.current.push(
      {
        eventType: 'FINISHING_PROJECT_SETUP',
        payload: { status: 'started', publicProjectId }
      },
      {
        eventType: 'FINISHING_PROJECT_SETUP',
        payload: { status: 'completed', publicProjectId }
      }
    );
  };

  const handleCreateProject = (payload: SocketStepEvent) => {
    if (payload.status === 'started' && payload.toolType) {
      toolType.current = payload.toolType;
      return;
    }

    if (payload.status !== 'completed') {
      return;
    }

    hasCreateCompleted.current = true;
    setTargetProgress(100);
    setCurrentLabel('Finishing Project Setup');
    enqueueFinishingStep(payload.publicProjectId ?? null);
  };

  const handleGenerateBrief = (payload: SocketStepEvent) => {
    if (payload.status === 'started') {
      const label =
        toolType.current === 'VIDEO_PLAN'
          ? 'Using Generated Brief'
          : 'Generating Brief';
      setCurrentLabel(label);
      setTargetProgress(50);
      return;
    }

    if (payload.status === 'completed') {
      hasBriefCompleted.current = true;
    }
  };

  const handleGenerateScript = (payload: SocketStepEvent) => {
    if (payload.status === 'completed') {
      hasScriptCompleted.current = true;
    }

    if (payload.status !== 'started') {
      return;
    }

    if (toolType.current === 'SCRIPT') {
      hasBriefCompleted.current = true;
      setCurrentLabel('Using Generated Script');
      setTargetProgress(75);
      return;
    }

    if (hasBriefCompleted.current) {
      setCurrentLabel('Generating Script');
      setTargetProgress(75);
    }
  };

  const handleFinishingProjectSetup = (payload: SocketStepEvent) => {
    if (payload.status === 'started') {
      setCurrentLabel('Finishing Project Setup');
    }
  };

  const handleOneEvent = (eventType: string, payload: SocketStepEvent) => {
    if (
      payload.publicProjectId &&
      payload.publicProjectId !== expectedPublicProjectId
    ) {
      setError('Import failed: mismatched project ID.');
      return;
    }

    if (payload.status === 'failed') {
      setError(payload.error || `Step ${eventType} failed`);
      return;
    }

    const handlerMap: Record<string, (payload: SocketStepEvent) => void> = {
      'PUBLIC_TO_PLATFORM_PROJECT|CREATE_PROJECT': handleCreateProject,
      'PUBLIC_TO_PLATFORM_PROJECT|GENERATE_BRIEF': handleGenerateBrief,
      'PUBLIC_TO_PLATFORM_PROJECT|GENERATE_SCRIPT': handleGenerateScript,
      FINISHING_PROJECT_SETUP: handleFinishingProjectSetup
    };

    handlerMap[eventType]?.(payload);
  };

  const processQueue = async () => {
    if (isProcessing.current) return;
    isProcessing.current = true;

    while (pendingEvents.current.length > 0) {
      const { eventType, payload } = pendingEvents.current.shift()!;
      handleOneEvent(eventType, payload);
      await sleep(2000);
    }

    isProcessing.current = false;
  };

  useEffect(() => {
    if (!socket || !expectedPublicProjectId) return;
    socket.connect();

    const handleConnect = () => console.log('Socket connected');
    const handleDisconnect = () => console.log('Socket disconnected');

    socket.on('connect', handleConnect);
    socket.on('connect_error', handleDisconnect);
    socket.on('error', handleDisconnect);
    socket.on('disconnect', handleDisconnect);

    STEPS.slice(0, 3).forEach(({ eventType }) => {
      socket.on(eventType, (payload: SocketStepEvent) => {
        pendingEvents.current.push({ eventType, payload });
        processQueue();
      });
    });

    return () => {
      socket.off('connect', handleConnect);
      socket.off('connect_error', handleDisconnect);
      socket.off('error', handleDisconnect);
      socket.off('disconnect', handleDisconnect);
    };
  }, [socket]);

  useEffect(() => {
    const t = setTimeout(() => setTargetProgress(25), 10);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (targetProgress <= 0) return;
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= targetProgress) {
          clearInterval(interval);
          return prev;
        }
        return Math.min(prev + 1, targetProgress);
      });
    }, 20);
    return () => clearInterval(interval);
  }, [targetProgress]);

  useEffect(() => {
    if (
      currentLabel === 'Finishing Project Setup' &&
      progress === 100 &&
      videoId
    ) {
      history.replace(`/pro/builder/${videoId}#1`);
      window.location.reload();
    }
  }, [currentLabel, progress, videoId, history]);

  const handleRetry = async () => {
    setError(null);
    setLoading(true);
    pendingEvents.current = [];
    isProcessing.current = false;
    hasBriefCompleted.current = false;
    hasScriptCompleted.current = false;
    hasCreateCompleted.current = false;
    setCurrentLabel('Creating Project');
    setProgress(25);
    setTargetProgress(25);

    socket.connect(); // reconnect socket
    await importProject(); // retry actual import logic
  };

  const handleCancel = () => {
    setError(null);
    pendingEvents.current = [];
    isProcessing.current = false;
    socket.disconnect();
    history.push('/home');
  };

  const stepIndex = STEPS.findIndex((step) => step.label === currentLabel);
  const displayStep = stepIndex === -1 ? 1 : stepIndex + 1;

  return (
    <>
      {!error && (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-white">
          <div className="w-full max-w-xl px-4">
            <div className="mb-12 text-center">
              <div className="mb-6 flex h-24 w-24 items-center justify-center animate-scaleIn mx-auto">
                <img src={SHOOTSTA_LOGO} alt="Logo" width={90} height={90} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 animate-fadeInUp">
                {currentLabel}
              </h2>
            </div>

            <div className="mb-6 h-2 w-full overflow-hidden rounded bg-gray-200">
              <div
                className="h-full rounded bg-[#27baa1] transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            <div className="mb-8 flex w-full justify-between">
              {STEPS.map((step, idx) => {
                const threshold = (idx / (STEPS.length - 1)) * 100;
                const isGreen = progress >= threshold;
                return (
                  <div
                    key={step.eventType}
                    className="flex flex-col items-center"
                  >
                    <div
                      className={`mb-2 h-4 w-4 rounded-full ${isGreen ? 'bg-[#27baa1]' : 'bg-gray-300'}`}
                    />
                    <span className="hidden text-xs text-gray-500 sm:inline">
                      {idx + 1}
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mb-6 text-center text-sm text-gray-500">
              Step {displayStep} of {STEPS.length}
            </div>

            <div className="flex items-center justify-center gap-2 text-sm text-yellow-600">
              <AlertCircle className="h-4 w-4" />
              <p>Please do not refresh the page while processing</p>
            </div>
          </div>
        </div>
      )}

      <ImportFailedModal
        visible={!!error}
        onConfirmation={handleRetry}
        onClose={handleCancel}
      />
    </>
  );
}
